import { UserRequest } from 'src/requests/user';
import { dbPromise } from 'src/state/indexdb';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserRecent, UserRecentsInterface } from './types';

async function getStateFromIndexedDB() {
  const db = await dbPromise;
  return db.get('user-recents', 'recents') || {};
}

async function setStateToIndexedDB(state: any) {
  const db = await dbPromise;
  await db.put('user-recents', state, 'recents');
}

async function removeStateFromIndexedDB() {
  const db = await dbPromise;
  await db.delete('user-recents', 'recents');
}

export const useRecentStore = create(
  persist<UserRecentsInterface>(
    (set) => ({
      recents: {},
      fetch: async () => {
        const recents = await UserRequest.getUserRecents({});
        const { owner, workspace } = recents[0] || {};
        set((state) => {
          return {
            recents: {
              ...state.recents,
              [workspace]: {
                ...(state.recents[workspace] || {}),
                [owner]: recents,
              },
            },
          };
        });
      },
      addRecent: (
        workspace: string,
        owner: string,
        recent: Partial<UserRecent>,
      ) =>
        set((state) => {
          const currentRecents = (
            state.recents[workspace]?.[owner] || []
          ).filter((c) => c.url !== recent.url);
          const updatedRecents = [
            ...currentRecents,
            {
              ...recent,
              owner,
              workspace,
              lastAccessAt: new Date(),
            } as UserRecent,
          ].sort(
            (a, b) =>
              new Date(b.lastAccessAt).getTime() -
              new Date(a.lastAccessAt).getTime(),
          );

          return {
            recents: {
              ...state.recents,
              [workspace]: {
                ...(state.recents[workspace] || {}),
                [owner]: updatedRecents,
              },
            },
          };
        }),
      removeRecent: (workspaceId: string, userId: string, id: string) =>
        set((state) => {
          const currentRecents = state.recents[workspaceId]?.[userId] || [];
          const updatedRecents = currentRecents.filter(
            (recent) => recent._id !== id,
          );

          return {
            recents: {
              ...state.recents,
              [workspaceId]: {
                ...(state.recents[workspaceId] || {}),
                [userId]: updatedRecents,
              },
            },
          };
        }),
    }),
    {
      name: 'recents-store',
      getStorage: () => ({
        getItem: async (key) => {
          const state = await getStateFromIndexedDB();
          return JSON.stringify(state);
        },
        setItem: async (key, value) => {
          const state = JSON.parse(value);
          await setStateToIndexedDB(state);
        },
        removeItem: async (key) => {
          await removeStateFromIndexedDB();
        },
      }),
    },
  ),
);
